import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { reactQueryPost } from "@circle-react/helpers/backendRequestHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useState } from "react";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
// eslint-disable-next-line no-restricted-imports -- Compass useToast has a different API (toast.add); shared OTP screens still use the legacy hook.
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import type { OtpChallengeFlow } from "@/react/contexts/OtpChallengeContext";
import type { CurrentCommunity } from "@/react/types/CurrentCommunity";

import { OTPInput } from "../shared/Otp/OtpInput";
import { OtpFooter } from "./components/OtpFooter";
import { OtpHeader } from "./components/OtpHeader";
import { OtpLoading } from "./components/OtpLoading";
import { OtpSubheader } from "./components/OtpSubheader";
import { OtpTitle } from "./components/OtpTitle";
import { useOtpChallengeTracker } from "./useOtpChallengeTracker";

interface OtpChallengeContentProps {
  className?: string;
  community?: CurrentCommunity | null;
  email?: string;
  error?: boolean | null;
  flow?: OtpChallengeFlow;
  includeBorder?: boolean;
  isLoading?: boolean;
  onCancel: () => void;
  onResendError?: (message: string) => void;
  onResendSuccess?: (message: string) => void;
  onSignInWithPassword?: () => void;
  onSkip?: () => void;
  onSubmit: (otpCode: string) => void | Promise<void>;
  showHeader?: boolean;
  showSignInWithPassword?: boolean;
  showSubheaderTitle?: boolean;
  showTitle?: boolean;
  subheaderEmailSuffix?: string;
  subheaderTitle?: string;
  useEntryFlowBranding?: boolean;
}

interface ResendOtpResult {
  message: string;
}

interface ResendOtpRequestBody {
  user: {
    email: string;
  };
}

const ALLOWED_RESEND_COUNT = 1;

export const OtpChallengeContent = ({
  email,
  flow,
  onCancel,
  onSubmit,
  isLoading = false,
  error,
  showTitle = true,
  showHeader = true,
  showSubheaderTitle = false,
  showSignInWithPassword = false,
  onSignInWithPassword,
  onSkip,
  onResendSuccess,
  onResendError,
  includeBorder = true,
  community = null,
  className,
  subheaderEmailSuffix,
  subheaderTitle,
  useEntryFlowBranding = false,
}: OtpChallengeContentProps) => {
  const toast = useToast();
  const { currentCommunity: defaultCurrentCommunity } =
    useCurrentCommunityContext();

  const currentCommunity = community || defaultCurrentCommunity;

  // Sign-in/sign-up callers show the same "Welcome to {community}" branding
  // here that the entry-form step already shows, since this step replaces that
  // form in place rather than opening a separate page (see
  // determineTitleAndSubtitle). Other callers (e.g. post-auth OTP
  // confirmation) keep the generic "Check your inbox" copy, which fits a
  // context where the user is already signed in.
  const shouldShowEntryFlowBranding =
    useEntryFlowBranding &&
    Boolean(currentCommunity?.growth_otp_first_member_auth_enabled);
  const otpTitle = shouldShowEntryFlowBranding
    ? t("authentication_app.signup.title.welcome_community", {
        community_name: currentCommunity?.name,
      })
    : undefined;
  const otpSubtitle = shouldShowEntryFlowBranding
    ? t("authentication_app.signup.subtitle_otp_first")
    : undefined;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCount, setResendCount] = useState(0);

  const {
    trackCodeSubmitted,
    trackSucceeded,
    trackFailed,
    trackResendClicked,
    trackSignInWithPasswordClicked,
    trackSkipClicked,
  } = useOtpChallengeTracker(flow);

  const resendOtpMutation = useMutation({
    mutationFn: (body: ResendOtpRequestBody) => {
      setResendCount(resendCount + 1);
      return reactQueryPost(authenticationAppRoutes.resendOtp(), body);
    },
    onSuccess: (data: ResendOtpResult) => {
      if (onResendSuccess) {
        onResendSuccess(data?.message);
      } else {
        toast.success(data?.message);
      }
    },
    onError: (error: unknown) => {
      const err = error as { body?: { error?: string }; message?: string };
      const message =
        err.body?.error ||
        err.message ||
        t("otp_challenge_modal.resend_failed");
      if (onResendError) {
        onResendError(message);
      } else {
        toast.error(message);
      }
    },
  });

  const handleSubmit = async (otp: string) => {
    setIsSubmitting(true);
    trackCodeSubmitted();
    try {
      await onSubmit(otp);
      trackSucceeded();
    } catch {
      trackFailed();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = () => {
    if (!email) {
      return;
    }

    trackResendClicked();
    resendOtpMutation.mutate({ user: { email } });
  };

  const isResending = resendOtpMutation.isPending;
  const isDisabled = isSubmitting || isLoading || isResending;
  const isAuthenticating = isSubmitting || isLoading;

  return (
    <div
      className={classNames(
        "flex h-full flex-col items-center justify-center",
        className,
      )}
    >
      {showTitle && (
        <OtpTitle
          currentCommunity={currentCommunity}
          title={otpTitle}
          subtitle={otpSubtitle}
        />
      )}
      <div
        className={classNames(
          "text-primary",
          "w-full rounded-lg p-6 sm:w-[468px]",
          includeBorder ? "border border-primary" : "",
        )}
      >
        {showHeader && (
          <OtpHeader
            email={email}
            onCancel={onCancel}
            isDisabled={isDisabled}
          />
        )}

        <div className="space-y-6">
          <OtpSubheader
            email={email}
            emailSuffix={subheaderEmailSuffix}
            showTitle={
              showSubheaderTitle ||
              shouldShowEntryFlowBranding ||
              Boolean(subheaderTitle)
            }
            showSecureAccountCopy={shouldShowEntryFlowBranding}
            title={subheaderTitle}
          />

          <OTPInput
            onComplete={handleSubmit}
            disabled={isDisabled}
            error={!!error}
            className="mx-auto max-w-md"
          />

          {isAuthenticating ? (
            <OtpLoading />
          ) : (
            <OtpFooter
              handleResend={handleResend}
              allowResend={resendCount < ALLOWED_RESEND_COUNT}
              isLoading={isResending}
              showSignInWithPassword={showSignInWithPassword}
              onSignInWithPassword={
                onSignInWithPassword
                  ? () => {
                      trackSignInWithPasswordClicked();
                      onSignInWithPassword();
                    }
                  : undefined
              }
              onSkip={
                onSkip
                  ? () => {
                      trackSkipClicked();
                      onSkip();
                    }
                  : undefined
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

OtpChallengeContent.displayName = "OtpChallengeContent";
